import { useEffect, useMemo, useState } from 'react';

import type { TourStop } from '#components/tour/CreateTourDialog/TourStop.ts';
import type { OrderSummary } from '#domain/Order.ts';
import type { TourDetails } from '#domain/Tour.ts';

import { mapOrdersToOrderedTourStops } from '../utils/mapOrdersToOrderedTourStops.ts';

const getMisplacedStopIds = (stops: TourStop[]) =>
    stops.reduce<string[]>((ids, item, index) => {
        if (item.type === 'unload') {
            const loadIndex = stops.findIndex(el => el.group === item.group && el.type === 'load');
            if (loadIndex !== -1 && index < loadIndex) {
                ids.push(item.id);
            }
        }
        return ids;
    }, []);

export const useDuplicateTourStops = (
    isOpen: boolean,
    selectedOrders: OrderSummary[],
    selectedTourDetails: TourDetails
) => {
    const selectedTourStops = selectedTourDetails.stops;
    const [sortableTourStops, setSortableTourStops] = useState<TourStop[]>([]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const nextStops = mapOrdersToOrderedTourStops(selectedOrders, selectedTourStops);
        setSortableTourStops(prevStops => {
            const isSame =
                prevStops.length === nextStops.length &&
                prevStops.every((stop, index) => stop.id === nextStops[index].id);
            return isSame ? prevStops : nextStops;
        });
    }, [isOpen, selectedOrders, selectedTourStops]);

    const misplacedStopIds = useMemo(
        () => (isOpen ? getMisplacedStopIds(sortableTourStops) : []),
        [isOpen, sortableTourStops]
    );

    return {
        sortableTourStops,
        setSortableTourStops,
        misplacedStopIds,
        isMisplaced: misplacedStopIds.length > 0,
    };
};
